// Shared CTE column projection. Engines call this from `emitCteColumnList`
// (and from `inferSubqueryColumns.pushAliasColumns`) so the same precedence
// order applies everywhere:
//
//   1. explicit `WITH foo (a, b) AS …` column list wins
//   2. RETURNING column list extracted by `cteScope` lands in the same
//      `columns` field, so 1+2 share the early-return
//   3. otherwise infer from the inner SELECT body via `inferSubqueryColumns`
//
// When `bodyRange` is missing (e.g. fixture without inference context) the
// helper returns an empty list — the resolver's pre-CB1 behavior.
//
// CYCLE GUARD: `resolveCteColumns` participates in a mutual recursion with
// `inferSubqueryColumns` (via `pushAliasColumns`, which re-enters this fn for a
// CTE referenced inside another CTE's body). A self- or mutually-referential
// CTE — `WITH RECURSIVE t AS (SELECT * FROM t)` or a `WITH a AS (… b …), b AS
// (… a …)` pair — would otherwise stack-overflow on the keystroke hot path,
// rejecting the completion promise and silently disabling the provider. We
// thread a `visited` set (folded CTE names currently on the resolution stack)
// through the recursion: before re-entering a CTE we mark it visited, and if a
// CTE is already in `visited` we bail with `[]` instead of recursing.

import type {
  CteBinding,
  DatabaseEngineId,
} from '../../../core/engines';

import { inferSubqueryColumns, type InferTable } from './inferSubqueryColumns';

export function resolveCteColumns(args: {
  readonly cte: CteBinding;
  readonly source: string;
  readonly engineId: DatabaseEngineId;
  readonly snapshotTables: ReadonlyArray<InferTable>;
  readonly cteBindings: readonly CteBinding[];
  /** Folded names of CTEs currently being resolved on the recursion stack.
   *  Defaults to a fresh empty Set at the top-level entry; callers in the
   *  mutual recursion pass the live set so a CTE already in flight is not
   *  re-entered (see CYCLE GUARD above). */
  readonly visited?: ReadonlySet<string>;
}): readonly string[] {
  if (args.cte.columns) return args.cte.columns;
  if (!args.cte.bodyRange) return [];

  const folded = args.cte.name.toLowerCase();
  const visited = args.visited ?? new Set<string>();
  if (visited.has(folded)) return [];
  const nextVisited = new Set(visited);
  nextVisited.add(folded);

  return inferSubqueryColumns({
    source: args.source,
    bodyRange: args.cte.bodyRange,
    engineId: args.engineId,
    snapshotTables: args.snapshotTables,
    cteBindings: args.cteBindings,
    visited: nextVisited,
  });
}
